import type { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { Loader } from '@hotjar/hotsauce';
import { VerificationCodeField } from '@hotjar/webapp/components/VerificationCodeField';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { EmailVerificationComponentStyles } from './style';
import { useEmailVerificationComponent } from './useEmailVerificationComponent';
import type { EmailVerificationResult } from './types';

const { StyledError, StyledSuccess, StyledIcon, ProviderIcon, Link, ProviderLinks } =
  EmailVerificationComponentStyles;

interface EmailVerificationComponentProps {
  userEmail: string;
  onEmailVerified: (result: EmailVerificationResult) => void;
  onLoadingChange: (isLoading: boolean) => void;
  footer?: (sendVerificationEmail: () => void) => ReactElement;
  onEmailLinkClick?: (provider: string) => void;
}

export const EmailVerificationComponent = ({
  userEmail,
  onEmailVerified,
  onLoadingChange,
  footer,
  onEmailLinkClick,
}: EmailVerificationComponentProps): ReactElement => {
  const { t } = useTranslationWithPrefix('auth:verify-email-component');
  const {
    isSendEmailPending,
    sendEmailError,
    isSuccess,
    verificationId,
    hasVerifyError,
    handleVerificationCodeChange,
    handleOnVerificationCodeInput,
    markEmailVerifiedError,
    doSendVerificationEmail,
  } = useEmailVerificationComponent(onEmailVerified, onLoadingChange);

  return (
    <Loader size="xl" isLoading={isSendEmailPending}>
      {sendEmailError && <StyledError textColor="text_error">{t(sendEmailError)}</StyledError>}
      {!isSuccess && verificationId && (
        <>
          <p>
            <Trans i18nKey="auth:verify-email-component.verify-email-sent" values={{ userEmail }}>
              <strong>{{ userEmail }}</strong>
            </Trans>
          </p>
          <VerificationCodeField
            justify="flex-start"
            hasError={hasVerifyError}
            onChange={handleVerificationCodeChange}
            onInput={handleOnVerificationCodeInput}
          />
          {markEmailVerifiedError && (
            <StyledError textColor="text_error" data-testid="verify-email-otp-error">
              {t(markEmailVerifiedError)}
            </StyledError>
          )}
          <div>
            <ProviderLinks>
              <Link
                data-testid="email-link-gmail"
                href="https://mail.google.com/"
                onClick={() => onEmailLinkClick && onEmailLinkClick('gmail')}
                target="_blank"
                rel="noreferrer"
              >
                <ProviderIcon
                  // FIXME: global-require
                  // eslint-disable-next-line global-require
                  src={require('@hotjar/shared-assets/images/signup/gmail.png')}
                  width="16"
                />
                {t('open-gmail')}
              </Link>
              <Link
                data-testid="email-link-live"
                href="https://login.live.com/login.srf"
                onClick={() => onEmailLinkClick && onEmailLinkClick('outlook')}
                target="_blank"
                rel="noreferrer"
              >
                <ProviderIcon
                  // FIXME: global-require
                  // eslint-disable-next-line global-require
                  src={require('@hotjar/shared-assets/images/signup/outlook.png')}
                  width="16"
                />
                {t('open-outlook')}
              </Link>
            </ProviderLinks>
          </div>
          {footer && footer(doSendVerificationEmail)}
        </>
      )}
      {isSuccess && (
        <StyledSuccess textColor="text_success">
          <StyledIcon icon="check" inline />
          {t('verify-email-success')}
        </StyledSuccess>
      )}
    </Loader>
  );
};
